import {NonEmptyString, StringUnion, toOption, EmptyString} from 'Core/models/EmptyString';
import {sequenceS} from 'fp-ts/lib/Apply';
import {flow} from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {Lens} from 'monocle-ts';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

export class InvalidNewsEntry {
  public static fromNewsItem(newsItem: NewsEntry) {
    return new InvalidNewsEntry(newsItem.id, newsItem.title, newsItem.description);
  }

  public readonly key = 'InvalidNewsEntry';

  constructor(
    public readonly id: O.Option<string>,
    public readonly title: StringUnion,
    public readonly description: StringUnion
  ) {}
}

export class ValidNewsEntry {
  public static create(invalidNewsItem: InvalidNewsEntry) {
    const validationResults = sequenceS(O.option)({
      title: toOption(invalidNewsItem.title),
      description: toOption(invalidNewsItem.description),
    });
    const {id} = invalidNewsItem;

    return pipe(
      validationResults,
      O.fold(
        (): NewsEntry => invalidNewsItem,
        ({title, description}) => new ValidNewsEntry(id, title, description)
      )
    );
  }

  public readonly key = 'ValidNewsEntry';
  private constructor(
    public readonly id: O.Option<string>,
    public readonly title: NonEmptyString,
    public readonly description: NonEmptyString
  ) {}
}

export class NewsEntryWithError {
  public static create(errorMessage: string, validNewsEntry: ValidNewsEntry) {
    const {id, title, description} = validNewsEntry;
    return new NewsEntryWithError(errorMessage, id, title, description);
  }

  public readonly key = 'NewsEntryWithError';

  private constructor(
    public readonly errorMessage: string,
    public readonly id: O.Option<string>,
    public readonly title: NonEmptyString,
    public readonly description: NonEmptyString
  ) {}
}

const newsLens = Lens.fromProp<NewsEntry>();
const withNewsValidation = flow(InvalidNewsEntry.fromNewsItem, ValidNewsEntry.create);
const changeNewsItem = (transformNewsItem: (item: NewsEntry) => NewsEntry) => (existingNewsItem: NewsEntry) =>
  pipe(existingNewsItem, transformNewsItem, withNewsValidation);

export const changeTitle = flow(NonEmptyString.create, newsLens('title').set, changeNewsItem);
export const changeDescription = flow(NonEmptyString.create, newsLens('description').set, changeNewsItem);

export type NewsEntry = NewsEntryWithError | ValidNewsEntry | InvalidNewsEntry;

export type NewsItemOptions<TReturn> = ToMatchOptions<NewsEntry, 'key', TReturn>;

export const matchNewsEntry = <TReturn>(newsItem: NewsEntry, options: NewsItemOptions<TReturn>): TReturn => {
  switch (newsItem.key) {
    case 'InvalidNewsEntry':
      return options.InvalidNewsEntry(newsItem);
    case 'ValidNewsEntry':
      return options.ValidNewsEntry(newsItem);
    case 'NewsEntryWithError':
      return options.NewsEntryWithError(newsItem);
  }
};

export const isValid = (newsEntry: NewsEntry): newsEntry is ValidNewsEntry => newsEntry.key === 'ValidNewsEntry';
export const hasError = (newsEntry: NewsEntry): newsEntry is NewsEntryWithError =>
  newsEntry.key === 'NewsEntryWithError';

export const EmptyNewsItem = new InvalidNewsEntry(O.none, new EmptyString(), new EmptyString());
